import { useEffect, useState } from "react";
import { X, Plus, Key, EyeSlash, Eye, Gauge, GoogleLogo, Question } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, setUser as saveUser } from "@/lib/api";
import KeyInstructionsPopover from "@/components/KeyInstructionsPopover";
import KeyTestButton from "@/components/KeyTestButton";
import GoogleCalendarSetupGuide from "@/components/GoogleCalendarSetupGuide";

export function TopUpModal({ open, currentBalanceCents = 0, onClose, onSuccess }) {
  const [amount, setAmount] = useState("25");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const dollars = parseFloat(amount || "0");
    if (!dollars || dollars <= 0) return toast.error("Enter a positive USD amount.");
    setLoading(true);
    try {
      const r = await api.post("/usage/me/balance", { amount_cents: Math.round(dollars * 100) });
      saveUser(r.data);
      toast.success(`Added $${dollars.toFixed(2)} to your balance.`);
      onSuccess?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Top-up failed");
    } finally { setLoading(false); }
  };

  const presets = [10, 25, 50, 100];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="topup-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="modal-safe relative z-10 w-full max-w-md bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div>
            <div className="font-serif-display text-lg leading-none text-[#1C1B1A]">Top up credits</div>
            <div className="text-xs text-[#6B6A68] mt-1">Current balance: ${(currentBalanceCents / 100).toFixed(2)}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-[#6B6A68] leading-relaxed">
            <strong>MVP top-up:</strong> credits add to your balance instantly without payment. Stripe checkout arrives in Phase 2 — once live, this same flow becomes a paid charge.
          </p>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(String(p))}
                data-testid={`topup-preset-${p}`}
                className={`text-sm px-3 py-1.5 rounded-md border ${parseFloat(amount) === p ? "border-[#4A5D4E] bg-[#F0F1EE]" : "hairline hover:border-[#4A5D4E]/50"}`}>
                +${p}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-sm text-[#6B6A68] mb-1.5">Amount (USD)</label>
            <div className="flex items-center gap-2">
              <span className="text-[#6B6A68]">$</span>
              <input data-testid="topup-amount" type="number" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1 bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 bg-[#FDFCF8] border-t hairline">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
          <button type="submit" disabled={loading} data-testid="topup-submit"
            className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1.5">
            <Plus size={14} /> {loading ? "Adding…" : "Add credits"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function BYOKCard({ user, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/byok", { creator_api_key: key.trim() || null });
      saveUser(r.data);
      onUpdated?.(r.data);
      setKey("");
      setEditing(false);
      toast.success(r.data.has_byok ? "API key saved." : "API key removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove your API key? Calls will fall back to the platform key.")) return;
    setSaving(true);
    try {
      const r = await api.patch("/auth/byok", { creator_api_key: null });
      saveUser(r.data);
      onUpdated?.(r.data);
      toast.success("API key removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    } finally { setSaving(false); }
  };

  return (
    <div id="byok-card" className="bg-white border hairline rounded-xl p-6 scroll-mt-28" data-testid="byok-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <Key size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-1.5">
                Bring your own LLM key
                <KeyInstructionsPopover keyType="gemini_byok" testid="byok-instructions" onSaved={(u) => { saveUser(u); onUpdated?.(u); }} />
              </div>
              <div className="text-xs text-[#6B6A68] mt-0.5">
                {user?.has_byok
                  ? "Active — your silos call the LLM through your own provider account."
                  : "Optional. Without it, calls run on the platform key (and burn your platform credits)."}
              </div>
            </div>
            <div className="flex gap-2">
              {user?.has_byok && !editing && (
                <KeyTestButton keyType="gemini_byok" testid="byok-test" />
              )}
              {user?.has_byok && !editing && (
                <button onClick={clearKey} disabled={saving} data-testid="byok-clear" className="text-xs px-3 py-1.5 rounded-md text-[#C16E5A] hover:bg-[#FDF1ED]">Remove</button>
              )}
              {!editing && (
                <button onClick={() => setEditing(true)} data-testid="byok-edit"
                  className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
                  {user?.has_byok ? "Replace" : "Add key"}
                </button>
              )}
            </div>
          </div>
          {editing && (
            <div className="mt-3 flex items-stretch gap-2">
              <div className="flex-1 relative">
                <input
                  data-testid="byok-input"
                  type={showKey ? "text" : "password"}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="sk-… or AIza… or your provider key"
                  className="w-full bg-white border hairline rounded-md px-3 py-2 pr-10 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                />
                <button type="button" onClick={() => setShowKey((s) => !s)} className="absolute right-1.5 top-1.5 p-1.5 text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="byok-toggle-show">
                  {showKey ? <EyeSlash size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <button onClick={save} disabled={saving || !key.trim()} data-testid="byok-save"
                className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { setEditing(false); setKey(""); }} className="text-xs px-3 py-2 text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
            </div>
          )}
          {!editing && user?.has_byok && (
            <div className="mt-2 text-[11px] text-[#6B6A68] font-mono bg-[#F0F1EE] inline-block px-2.5 py-1 rounded">••••••••••••••• · stored securely</div>
          )}
          <p className="text-[11px] text-[#6B6A68] mt-3">
            Compatible with Anthropic (Claude), OpenAI (GPT), and Google (Gemini) API keys. Stored in your account — replace or remove anytime.
          </p>
        </div>
      </div>
      {/* Spending cap controls */}
      <SpendingCapBlock />
    </div>
  );
}

export function YouTubeKeyCard({ user, onUpdated }) {
  // Mirror of BYOKCard for the YouTube Data API v3 key. Stored on the same user
  // record (users.youtube_api_key) so this card and the YouTube Tool's in-silo
  // editor are the same source of truth — edits here reflect there instantly.
  const [editing, setEditing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const hasKey = !!user?.has_youtube_key;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/youtube-key", { youtube_api_key: key.trim() || null });
      saveUser(r.data);
      onUpdated?.(r.data);
      setKey("");
      setEditing(false);
      toast.success(r.data.has_youtube_key ? "YouTube key saved." : "YouTube key removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove your YouTube key? YouTube Tool pulls will stop working.")) return;
    setSaving(true);
    try {
      const r = await api.patch("/auth/youtube-key", { youtube_api_key: null });
      saveUser(r.data);
      onUpdated?.(r.data);
      toast.success("YouTube key removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    } finally { setSaving(false); }
  };

  return (
    <div id="yt-key-card" className="bg-white border hairline rounded-xl p-6 scroll-mt-28" data-testid="yt-key-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <Key size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-2 flex-wrap">
                YouTube Data API v3 key
                <KeyInstructionsPopover keyType="youtube" variant="button" label="How to get this key" testid="yt-key-instructions-ops" onSaved={(u) => { saveUser(u); onUpdated?.(u); }} />
              </div>
              <div className="text-xs text-[#6B6A68] mt-0.5">
                {hasKey
                  ? "Active — YouTube pulls (titles, transcripts, comments, channel libraries) work across every silo."
                  : "Required for YouTube pulls. Without it, the YouTube Tool inside silos can't fetch data."}
              </div>
            </div>
            <div className="flex gap-2">
              {hasKey && !editing && (
                <KeyTestButton keyType="youtube" testid="yt-key-test" />
              )}
              {hasKey && !editing && (
                <button onClick={clearKey} disabled={saving} data-testid="yt-key-clear" className="text-xs px-3 py-1.5 rounded-md text-[#C16E5A] hover:bg-[#FDF1ED]">Remove</button>
              )}
              {!editing && (
                <button onClick={() => setEditing(true)} data-testid="yt-key-edit-ops"
                  className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
                  {hasKey ? "Replace" : "Add key"}
                </button>
              )}
            </div>
          </div>
          {editing && (
            <div className="mt-3 flex items-stretch gap-2">
              <div className="flex-1 relative">
                <input
                  data-testid="yt-key-input-ops"
                  type={showKey ? "text" : "password"}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="AIzaSy…"
                  className="w-full bg-white border hairline rounded-md px-3 py-2 pr-10 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                />
                <button type="button" onClick={() => setShowKey((s) => !s)} className="absolute right-1.5 top-1.5 p-1.5 text-[#6B6A68] hover:text-[#1C1B1A]">
                  {showKey ? <EyeSlash size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <button onClick={save} disabled={saving || !key.trim()} data-testid="yt-key-save-ops"
                className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { setEditing(false); setKey(""); }} className="text-xs px-3 py-2 text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
            </div>
          )}
          {!editing && hasKey && (
            <div className="mt-2 text-[11px] text-[#6B6A68] font-mono bg-[#F0F1EE] inline-block px-2.5 py-1 rounded">••••••••••••••• · stored securely</div>
          )}
          <p className="text-[11px] text-[#6B6A68] mt-3">
            Separate from your LLM key. YouTube requires its own Data API v3 key (free within 10k units/day quota). Used across all your silos' YouTube Tool pulls.
          </p>

          {/* Google Calendar connection — Jul 2026 (Randy). Same frame as the
              YouTube key on purpose: both live in the same Google Cloud
              project, so the walkthroughs chain naturally. */}
          <GoogleCalendarConnectBlock />
        </div>
      </div>
    </div>
  );
}

export function GoogleCalendarConnectBlock() {
  // BYOK-style Google Calendar OAuth credentials (Client ID + Secret). The
  // full step-by-step lives in the shared GoogleCalendarSetupGuide modal —
  // the SAME guide the Staging AI Calendar opens, so both stay in sync.
  const [status, setStatus] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const load = () => api.get("/calendar/google/credentials").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const hasCreds = !!status?.has_credentials;
  const sharedActive = !!status?.shared_configured;
  return (
    <div className="mt-4 pt-4 border-t hairline" data-testid="gcal-connect-block">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-2 flex-wrap">
            <GoogleLogo size={14} weight="bold" /> Google Calendar connection
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              data-testid="gcal-howto-btn"
              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-[#4A5D4E]/40 text-[#4A5D4E] bg-[#F0F1EE]/60 hover:bg-[#F0F1EE] hover:border-[#4A5D4E] transition-colors font-medium"
            >
              <Question size={12} weight="bold" /> {sharedActive ? "How it works" : "How to set this up"}
            </button>
          </div>
          <div className="text-xs text-[#6B6A68] mt-0.5">
            {status?.connected
              ? `Active — connected as ${status.email || "your Google account"}. Sync from any silo's Staging → AI Calendar.`
              : sharedActive
                ? "Ready when you are. Open any silo → Staging → AI Calendar → Connect Google Calendar. ~15 seconds."
                : hasCreds
                  ? "Credentials saved — finish by hitting \"Connect Google Calendar\" inside your silo's Staging → AI Calendar."
                  : "Powers the AI Calendar's Google sync — your family's live calendar flows into your silo. Free, read-only, ~5 minutes to set up."}
          </div>
        </div>
        {!hasCreds && !sharedActive && (
          <button onClick={() => setGuideOpen(true)} data-testid="gcal-setup-open"
            className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
            Set up
          </button>
        )}
      </div>
      {hasCreds && !sharedActive && (
        <div className="mt-2 text-[11px] text-[#6B6A68] font-mono bg-[#F0F1EE] inline-block px-2.5 py-1 rounded" data-testid="gcal-block-status">
          {status.client_id_masked} · stored securely{status.connected ? " · connected" : ""}
        </div>
      )}
      <GoogleCalendarSetupGuide open={guideOpen} onClose={() => setGuideOpen(false)} onSaved={() => load()} />
    </div>
  );
}

function SpendingCapBlock() {
  // Feb 18, 2026 — Removed per user feedback. The cap blocked LLM calls
  // when hit, which was indistinguishable from simply running out of
  // balance. Same outcome, extra friction. The /api/auth/spending-cap
  // backend endpoint is retained (still returns the underlying balance
  // status) but no UI control surfaces it anymore.
  return null;
}
